import { GET_CONTACT_BY_ID } from '@zaplify/graphql';
import { MessageNotSentError, useMessageSending } from './use-message-sending';
import { useQuery } from '@apollo/client';
import { ChannelType } from '../../../types/channel-type';
import { MessageType as BEMessageType, PrettyConversation } from '@zaplify/services-messaging-shared';
import { ChannelType as BEChannelType } from '@zaplify/channel-accounts-shared';
import { ComposerMessage } from './composer-message';
import { MessageType } from '../../../types/message-type';
import { useCallback, useMemo, useState } from 'react';
import { createMastraClient } from '../../../pages/home/lib/mastra-client';
import { useAuth } from '../../../providers/authentication-provider';
import { useEmail } from '../../use-email';
import { htmlToText } from 'html-to-text';
import { useContactMessages } from '../use-contact-messages';

type MessageActionsProps = {
    contactId: string;
    composerMessage: ComposerMessage;
    setBody: (body: string) => void;
    setSubjectLine: (subjectLine: string) => void;
    setChannelType: (channelType: ChannelType) => void;
    removeDraftMessage: () => void;
    setDraftMessage: ({
        message,
        source,
        userPrompt,
    }: {
        message: ComposerMessage;
        source: 'user' | 'auto-generate' | 'regenerate';
        userPrompt?: string;
    }) => void;
    messageType: MessageType;
    onMessageSuccessfullySent: () => void;
};

interface GeneratedMessage {
    subjectLine?: string;
    content: string;
    goal?: string;
    assistantIdentifier: string;
    topics?: string;
}

const generateMessageInstructions = `\n\nYou need to send a message to the contact. All your output is visible to the recipient. Respond ONLY with the message you intend to send. No extra comments, instructions, or metadata.
You write like a real person—not a sales rep, not a bot. Your tone is casual, human, and concise. You care about sounding natural, not polished.

Your messages feel authentic and peer-to-peer:
 • 1-3 short sentences
 • ALWAYS match the ongoing conversation's language. Stick strictly to one language per message—if previous messages are in English, continue in English; if Spanish, continue in Spanish; if Swedish, continue in Swedish, etc.
 • Use "I" more often than "we" when sharing your ideas or suggesting next steps
 • Use contractions naturally ("I'd," "I'm"), but never use corporate buzzwords
 • Avoid filler words and excessive enthusiasm ("impressed," "fascinating," "thrilled," "always eager")
 • Don't force greetings or sign-offs—only include them when natural
 • Present one clear idea or question per message
 • Lightly personalize your reply (use their name or reference a previous point)

Understanding the conversation context:
 • Start by reading the conversation and identify the language of the conversation, make sure to respond in the same language.
 • Track who said what, when, and why. Keep a clear understanding of the authors, timing, topics, and follow-ups of each message, along with the conversation's overall purpose. When replying, focus on the latest message requiring a response or follow-up, always aiming to add value for both participants. Only revisit previous points if they clearly enhance the current conversation, and always introduce them naturally.
 • Consider carefully how much time has passed since each message. If a message is old and the conversation has paused or moved on, don't reply as if it just arrived. However, if the user asked something and hasn't heard back for a while, responding can still be valuable—just briefly acknowledge the delay in your reply.

Things you always avoid:
 • Excessive enthusiasm or eager energy
 • Stacked or multiple rapid-fire questions ("What about X? And Y?")
 • Generic flattery or filler ("great to connect")

Example of good vs. bad messaging:
 BAD: "Always great to hear ideas from someone using the product! We're definitely thinking about ways to make the daily goal more meaningful. Would love to hear what you're envisioning—maybe some reward mechanics or triggers? Maybe we could do a quick brainstorm session sometime?"
 GOOD: "Love that idea. I was thinking a simple progress bar, have you seen any apps that do this well?"

Now: write the next message you'd actually want to send.
`;

const emailInstructions = `\n\nWrite in the format <subjectLine>SOME SUBJECT LINE</subjectLine><body>SOME BODY<br><signature/></body>.\n It is important to include the signature placeholder <signature/> in the body of the email as it will be replaced with the actual signature.`;

export const useMessageActions = ({
    composerMessage,
    setBody,
    setSubjectLine,
    setChannelType,
    removeDraftMessage,
    setDraftMessage,
    messageType,
    contactId,
    onMessageSuccessfullySent,
}: MessageActionsProps) => {
    const [isGeneratingMessage, setIsGeneratingMessage] = useState(false);
    const { emailAccount } = useEmail();
    const {
        authState: { tokenResult, userId },
    } = useAuth();
    const { data: contact } = useQuery(GET_CONTACT_BY_ID, {
        variables: { id: contactId },
    });
    const { messagesNotesAndPendingMessages } = useContactMessages({ contactId });
    const { sendMessage, addNote, isSending, setIsSending } = useMessageSending({
        contactId: contact?.Contacts_by_pk?.id,
    });

    const messages = useMemo(() => {
        return messagesNotesAndPendingMessages.filter((message) => message.__typename === 'Messages');
    }, [messagesNotesAndPendingMessages]);

    const prospectId = useMemo(() => contact?.Contacts_by_pk?.prospectId, [contact?.Contacts_by_pk?.prospectId]);

    const previewRegeneratedMessageContentStreaming = useCallback(
        async ({
            prospectId,
            channelType,
            actionType,
            feedback,
            message,
        }: {
            prospectId: string;
            channelType?: BEChannelType;
            actionType: MessageType;
            feedback: string;
            message: { subjectLine?: string; content: string };
        }) => {
            if (!contact) {
                console.warn('Not generating message because contact not found');
                return;
            }
            const mastra = createMastraClient({ token: tokenResult?.token });
            const agent = await mastra.getAgent('contactMessageGenerationAgent');
            const conversation = new PrettyConversation()
                .create(messages)
                .getFormattedStringShort({ nMessages: 8, maxStringLengthPerMessage: 300 });

            const response = await agent.stream({
                threadId: `contact-${contactId}`,
                resourceId: userId,
                messages: [
                    {
                        role: 'user',
                        content: `You're having a conversation over ${
                            channelType === BEChannelType.EMAIL ? 'email' : 'LinkedIn chat'
                        }.
                        ${conversation?.length ? `\n\nLast few messages in the conversation: \n${conversation}` : ''}
                        ${
                            feedback?.length
                                ? `\n\nVery important additional context of how to continue the conversation: ${feedback}`
                                : ''
                        }
                        ${generateMessageInstructions}
                        ${channelType === BEChannelType.EMAIL ? emailInstructions : ''}
                        `,
                    },
                ],
            });
            let content = '';
            let finalSubjectLine = message.subjectLine;
            let finalContent = '';

            await response.processDataStream({
                onTextPart(value) {
                    console.log('🤖 ~ onTextPart ~ value:', value);
                    // Update the content text - always show text immediately
                    content += value;

                    // Try to parse email format if it's an email channel
                    if (channelType === BEChannelType.EMAIL) {
                        try {
                            // Try to extract subject line if available
                            const subjectMatch = content.match(/<subjectLine>(.*?)(?:<\/subjectLine>|$)/s);
                            const bodyMatch = content.match(/<body>(.*?)(?:<\/body>|$)/s);

                            if (subjectMatch && bodyMatch) {
                                // Both subject and body found
                                finalSubjectLine = subjectMatch[1];
                                finalContent = bodyMatch[1];
                                setSubjectLine(finalSubjectLine);
                                setBody(finalContent);
                            } else if (subjectMatch) {
                                // Only subject found so far
                                finalSubjectLine = subjectMatch[1];
                                setSubjectLine(finalSubjectLine);
                                // Clear body or keep it empty until we get body content
                                if (!content.includes('<body>')) {
                                    setBody('');
                                }
                            } else if (bodyMatch) {
                                // Only body found
                                finalContent = bodyMatch[1];
                                setBody(finalContent);
                            } else if (content.includes('<subjectLine>') || content.includes('<body>')) {
                                // Tags detected but content not ready yet, don't update anything
                                // This handles cases where tags are split across chunks
                            } else {
                                // No email format detected, treat as regular content
                                finalContent = content;
                                setBody(finalContent);
                            }
                        } catch (error) {
                            // Parsing failed, set everything to body
                            finalContent = content;
                            setBody(content);
                        }
                    } else {
                        // Not an email, just set body as before
                        finalContent = content;
                        setBody(content);
                    }
                },
            });
            if (finalContent.includes('<signature/>')) {
                finalContent = emailAccount?.signature
                    ? finalContent.replace(/<signature\/>/gi, emailAccount?.signature)
                    : finalContent.replace(/<br><signature\/>/gi, '');
            }

            return {
                content: finalContent,
                subjectLine: finalSubjectLine,
            };
        },
        [contactId, tokenResult?.token, userId, emailAccount?.signature]
    );

    const regenerateMessage = async ({
        feedback,
        path,
        autoGenerate,
        overWriteChannelType,
    }: {
        feedback?: string;
        path?: string;
        autoGenerate?: boolean;
        overWriteChannelType?: ChannelType;
    }) => {
        if (!overWriteChannelType && composerMessage.channelType === ChannelType.NOTE) {
            console.log('Not generating message because channel type is note');
            return;
        }

        let beChannelType: BEChannelType;
        if (overWriteChannelType) {
            if (overWriteChannelType === ChannelType.EMAIL) {
                beChannelType = BEChannelType.EMAIL;
            } else {
                beChannelType = BEChannelType.LINKEDIN;
            }
        } else {
            beChannelType =
                composerMessage.channelType === ChannelType.EMAIL ? BEChannelType.EMAIL : BEChannelType.LINKEDIN;
        }
        const feChannelType = beChannelType === BEChannelType.EMAIL ? ChannelType.EMAIL : ChannelType.LINKEDIN;

        setIsGeneratingMessage(true);
        try {
            const regeneratedMessage = await previewRegeneratedMessageContentStreaming({
                prospectId,
                channelType: beChannelType,
                actionType: messageType as BEMessageType,
                feedback,
                message: {
                    content: composerMessage.content,
                    subjectLine: composerMessage.subjectLine,
                },
            });
            setDraftMessage({
                message: {
                    content: regeneratedMessage.content,
                    subjectLine: regeneratedMessage.subjectLine,
                    channelType: feChannelType,
                },
                source: autoGenerate ? 'auto-generate' : 'regenerate',
                userPrompt: feedback || '',
            });
            setBody(regeneratedMessage.content);
            setSubjectLine(regeneratedMessage.subjectLine);
            setChannelType(feChannelType);
        } catch (error) {
            console.error('Failed to regenerate message:', error);
            throw new MessageNotSentError('An error occurred when trying to regenerate your message.');
        } finally {
            setIsGeneratingMessage(false);
        }
    };

    const primaryAction = async (options: { omitMessage?: boolean } = { omitMessage: false }) => {
        if (!prospectId || !contact) return;

        const tempMessage = {
            content: composerMessage.content,
            subjectLine: composerMessage.subjectLine,
            channelType: composerMessage.channelType,
        };
        setBody('');
        setSubjectLine('');
        setIsSending(true);
        try {
            if (messageType === MessageType.note) {
                await addNote({
                    content: composerMessage.content,
                    title: undefined,
                });
            } else {
                // const suggestedChannelType = outreachSuggestionMessage?.channelType;
                const messageToSend = {
                    body: options.omitMessage ? '' : composerMessage.content,
                    subjectLine: options.omitMessage ? '' : composerMessage.subjectLine,
                    actionType: messageType,
                    // messageId: outreachSuggestionMessage?.id,
                    messageId: undefined,
                };
                await sendMessage({
                    message: messageToSend,
                    recipient: {
                        linkedinUserId: contact.Contacts_by_pk?.linkedinUserId,
                        prospectId,
                        prospectName: contact.Contacts_by_pk?.fullName,
                    },
                    onError: () => {
                        console.error('Message send failed');
                    },
                    onSuccess: () => {
                        onMessageSuccessfullySent();
                    },
                    // suggestedChannelType,
                });
            }
            removeDraftMessage();
            console.log('Message sent successfully');
        } catch (error) {
            console.error('Failed to send message:', error);
            setBody(tempMessage.content);
            setSubjectLine(tempMessage.subjectLine);
            setChannelType(tempMessage.channelType);
        } finally {
            setTimeout(() => {
                setIsSending(false);
            }, 2000);
        }
    };

    const handleGenerateMessageAsEmail = () => {
        try {
            let newContent = composerMessage.content;
            const plainSignature = htmlToText(emailAccount?.signature) || '';
            if (
                composerMessage.content.includes(plainSignature) ||
                composerMessage.content.includes(emailAccount?.signature) ||
                !plainSignature
            ) {
                return;
            } else {
                newContent = newContent + '<br>' + emailAccount.signature;
            }
            setBody(newContent);
            setChannelType(ChannelType.EMAIL);
        } catch (error) {
            console.error('Failed to generate email message:', error);
        }
    };

    return {
        regenerateMessage,
        primaryAction,
        handleGenerateMessageAsEmail,
        isSending,
        isGeneratingMessage,
    };
};
